// A message's thread in the side panel (M2-06; spec 06: "the side panel is where threads open;
// the main area never navigates away for a reply"). The `thread` focus key names the root
// message; the root and its replies come from the open topic's cached messages (no extra
// call), and replies post with `parent_message_id`. THREAD-READS: the bell follows this reply
// thread (no longer its whole topic), and an open thread is read up to its newest reply.
// CHAT-CONTROLS: "Also send to #channel" (Slack's box; the spec is silent) posts the reply in
// the thread and a copy on the channel's main line, quoting the root. CHAT-THREAD-AGENTS: the
// server makes the copy (`also_to_channel`), so it wakes no agent.
import {
  Button,
  Checkbox,
  Composer,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Field,
  FieldLabel,
  ThreadPanel,
  useIsMobile,
} from "@companyos/ui";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { PanelProps } from "../../extensions/registry";
import { useFocus } from "../../focus/focus";
import { useShellState } from "../../shell/shell-state";
import { AddresseeChip, conversationVersion, useAddressee } from "./addressee";
import { AgentWorkingLine } from "./agent-wakes";
import { AgentStatusLine } from "./cards";
import { FileDropZone, StagedStrip, useStagedFiles } from "./chat-files";
import {
  channelLabel,
  rootIdOf,
  runsInTopic,
  threadAsks,
  threadRuns,
  threadsOf,
} from "./chat-model";
import { withDraftKey } from "./chat-optimistic";
import { channelQuery, chatKeys, locateQuery, messagesQuery, topicsQuery } from "./chat-queries";
import { useBroadcastMentionables, useDraft, useRecoveredDraft, useReminders } from "./chat-switch";
import { useSendLater } from "./chat-switch-b";
import {
  flatMessages,
  toChatMessages,
  useMessageActions,
  usePost,
  useRuns,
  useSeatLabels,
} from "./conversation";
import { useSuggestions } from "./suggestion-cards";
import { ThreadAgentChips } from "./thread-agents";
import { useThreadReadActions, useThreadReads } from "./thread-reads";
import { useChat, useThreadMentionables } from "./use-chat";

export function ThreadSidePanel({ focus }: PanelProps) {
  const chat = useChat();
  const { api, co } = chat;
  const params = useParams({ strict: false }) as { channelId?: string; topicId?: string };
  const channelId = params.channelId ?? "";
  const channel = useQuery({ ...channelQuery(api, co, channelId), enabled: Boolean(channelId) });
  const topics = useQuery({ ...topicsQuery(api, co, channelId), enabled: Boolean(channelId) });
  const queryClient = useQueryClient();
  const rootId = focus.thread;
  // The channel's stream (CHAT-UX) mixes topics: the root's topic is the one whose latest page
  // (already in the cache: the person clicked the message) holds it.
  const fromStream = useMemo(() => {
    if (!rootId || params.topicId || !topics.data) return undefined;
    return topics.data.data.find((t) =>
      queryClient
        .getQueryData<{ data: { id: string }[] }>([...chatKeys.messages(co, t.thread_id), "latest"])
        ?.data.some((m) => m.id === rootId),
    );
  }, [rootId, params.topicId, topics.data, queryClient, co]);
  const dmPlace = channel.data?.kind === "dm" || channel.data?.kind === "group_dm";
  const topic =
    topics.data?.data.find((t) => t.id === params.topicId) ??
    // CHAT-TOPICS-SEPARATE: a channel with no topic in the route shows its own conversation.
    (params.topicId === undefined && !dmPlace
      ? topics.data?.data.find((t) => t.is_main)
      : undefined) ??
    fromStream ??
    (channel.data?.kind === "dm" || channel.data?.kind === "group_dm"
      ? topics.data?.data[0]
      : undefined);
  // CHAT-TOPICS-SEPARATE: an open topic the caller has not joined shows no messages here either
  // (the main pane holds its join prompt).
  // (The agency operator, with no membership here, reads without joining.)
  const notJoined =
    topic !== undefined && !topic.is_member && !topic.is_main && !dmPlace && chat.meId !== null;
  // CHAT-SWITCH-B: the message a link points at (`m`, a reply or the root itself), highlighted.
  const search = useSearch({ strict: false }) as { m?: unknown };
  const linked = typeof search.m === "string" && /^[\w-]{1,64}$/.test(search.m) ? search.m : null;
  // A root the loaded pages do not hold (an old one, or a topic outside the listed ones) is
  // located directly (`messages.locate`): its thread, the root and its replies, however old.
  const [locatedRoot, setLocatedRoot] = useState<string | null>(null);
  // The linked message first when there is one (a reply's answer is a window of replies around
  // it, however long the thread); the root itself when there is none, or the link is to another
  // thread (an `m` left over from an earlier link).
  // (Never for a topic the caller has not joined: its messages stay behind the join prompt.)
  const wanted = Boolean(rootId) && locatedRoot === rootId && !notJoined;
  const viaLink = useQuery({
    ...locateQuery(api, co, linked ?? ""),
    enabled: wanted && linked !== null && linked !== rootId,
  });
  const linkFits = viaLink.data?.root_id === rootId;
  const viaRoot = useQuery({
    ...locateQuery(api, co, rootId ?? ""),
    enabled:
      wanted &&
      (linked === null || linked === rootId || viaLink.isError || viaLink.isSuccess) &&
      !linkFits,
  });
  const locate = linkFits ? viaLink : viaRoot;
  // (Asked about the panel's message itself, the answer names its root: a reply's is another.)
  const loc =
    locate.data && (locate.data.root_id === rootId || locate === viaRoot) ? locate.data : undefined;
  const threadId = notJoined ? "" : (topic?.thread_id ?? loc?.thread_id ?? "");
  const messages = useInfiniteQuery({
    ...messagesQuery(api, co, threadId),
    enabled: Boolean(threadId),
  });
  const list = useMemo(() => flatMessages(messages.data), [messages.data]);
  const split = useMemo(() => threadsOf(list), [list]);
  const runs = useRuns(chat);
  const seats = useSeatLabels(chat);
  // THREAD-READS: the bell follows this reply thread (Slack's "Follow thread"); with no row of
  // it in the viewer's threads, following is by participation (its root or a reply is theirs).
  const reads = useThreadReads(chat);
  const readActions = useThreadReadActions(chat);
  // The bell's local choice, for the root it was made on (a thread with no row yet).
  const [override, setOverride] = useState<{ root: string; on: boolean } | null>(null);
  const send = usePost(chat, { id: channelId });
  const [alsoChannel, setAlsoChannel] = useState(false);
  // CHAT-AGENT-IMAGES: a link to a reply (a notification, an older reply-to-a-reply) opens the
  // reply thread it belongs to, with every reply in it, never a lone reply as its own root.
  const listed = useMemo(() => {
    const byId = new Map(list.map((m) => [m.id, m]));
    const named = focus.thread ? byId.get(focus.thread) : undefined;
    const root = named ? byId.get(rootIdOf(named, byId)) : undefined;
    return root && !root.parent_message_id ? root : undefined;
  }, [list, focus.thread]);
  // CHAT-SWITCH-A loaded up to 20 pages back looking for an older root; CHAT-SWITCH-B asks where
  // it is instead (`messages.locate`: a reply's answer names its root), once the topic list or
  // the first page has not got it.
  const pagesLoaded = messages.data?.pages.length ?? 0;
  const lost =
    Boolean(rootId) &&
    !listed &&
    topics.isFetched &&
    (!topic || (pagesLoaded > 0 && !messages.isFetching));
  // Codex r4 #4: the root is loaded but the linked reply is not (an old reply of a long thread):
  // locate it too, and its window of replies joins the loaded ones.
  const linkMissing =
    Boolean(listed) &&
    linked !== null &&
    linked !== rootId &&
    !messages.isFetching &&
    !(split.replies.get(listed?.id ?? "") ?? []).some((r) => r.id === linked);
  useEffect(() => {
    if ((lost || linkMissing) && rootId) setLocatedRoot(rootId);
  }, [lost, linkMissing, rootId]);
  const rootRow = listed ?? loc?.root;
  const openRoot = rootRow?.id;
  // The loaded list keeps the replies current (sends and pings land there); a located root's
  // replies come with it, plus any the list has gained since.
  const replies = useMemo(() => {
    if (!rootRow) return [];
    const fromList = split.replies.get(rootRow.id) ?? [];
    if (!loc) return fromList;
    const seen = new Set(fromList.map((r) => r.id));
    return [...loc.replies.filter((r) => !seen.has(r.id)), ...fromList].sort((a, b) =>
      a.created_at.localeCompare(b.created_at),
    );
  }, [rootRow, loc, split]);
  const state = openRoot ? reads.get(openRoot) : undefined;
  const following =
    (override && override.root === openRoot ? override.on : undefined) ??
    state?.following ??
    (rootRow !== undefined &&
      chat.meId !== null &&
      (rootRow.author_membership_id === chat.meId ||
        replies.some((r) => r.author_membership_id === chat.meId)));
  // Open with the newest reply in view: read. Again whenever a reply arrives while it is open
  // (the refetch after the reply's ping brings its unread count back above zero).
  const unread = state?.unread ?? 0;
  const { markRead } = readActions;
  const signedIn = chat.meId !== null;
  useEffect(() => {
    if (!openRoot || !threadId || !signedIn || unread === 0) return;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    markRead(threadId, openRoot);
  }, [openRoot, threadId, unread, signedIn, markRead]);
  // AGENT-MENTION: the reply composer's @ menu says who a mention here would wake.
  // CHAT-SWITCH-A: `@channel` / `@here` and `/remind` here too, as in the main composer.
  const mentionables = useBroadcastMentionables(
    chat,
    channel.data,
    useThreadMentionables(chat, threadId || null),
  );
  const remind = useReminders(chat);
  // CHAT-SWITCH-B: a reply may wait too ("Send later"; not its channel copy).
  const later = useSendLater(chat, rootRow ? threadId || null : null, rootRow?.id ?? null, () =>
    addressee.outgoing(),
  );
  // THREAD-WAKE: the root and its replies are one conversation, with its own addressee.
  const dmLike = channel.data?.kind === "dm" || channel.data?.kind === "group_dm";
  const addressee = useAddressee(
    chat,
    // CHAT-THREAD-AGENTS: a DM's reply thread picks one participant too.
    threadId && openRoot ? threadId : null,
    openRoot ?? null,
    conversationVersion(rootRow ? [rootRow, ...replies] : replies),
  );
  // Escape lets go of the thread (not while a menu, dialog or list box has it).
  const { setFocus } = useFocus();
  const { setPanelOpen } = useShellState();
  const mobile = useIsMobile();
  const open = Boolean(focus.thread);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector(POPUPS)) return;
      const root = focus.thread;
      setFocus({ thread: null });
      setPanelOpen(false);
      // Back to the message the thread opened from, when it is on screen.
      const row = document.querySelector<HTMLElement>(
        `[data-slot="message-opener"] [data-slot="message"][data-message-id="${root}"]`,
      );
      row?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, focus.thread, setFocus, setPanelOpen]);
  const close = () => {
    setFocus({ thread: null });
    setPanelOpen(false);
  };
  // CHAT-FILES: the reply composer's staged files (paperclip, drop on the panel, paste).
  const staged = useStagedFiles(chat);
  // Files picked for one reply thread never ride along into another one opened in this panel.
  const { clear } = staged;
  useEffect(() => {
    if (rootId === undefined) return;
    return () => clear();
  }, [rootId, clear]);
  // CHAT-SWITCH-A: the reply's draft survives closing the thread (one per thread root).
  const replyDraft = useDraft(chat, rootId ? `reply:${rootId}` : null);
  // CHAT-SWITCH-B: a reply put back from a refused send shows its files and addressee again.
  useRecoveredDraft(replyDraft?.mount ?? null, replyDraft?.key ?? null, staged, addressee);
  const actions = useMessageActions(chat, threadId, list, (b) =>
    send(threadId, { markdown: b.markdown, cardRefs: b.cardRefs, parent: rootRow?.id }),
  );
  // COS-SLICE1: the follow-up cards under the root and its replies.
  const suggestions = useSuggestions(chat, threadId ? [threadId] : []);
  if (!focus.thread || !rootRow || !channel.data)
    return (
      <Empty data-panel="thread" className="m-4">
        <EmptyHeader>
          <EmptyTitle>Thread</EmptyTitle>
          <EmptyDescription>
            {notJoined
              ? "Join the topic to read this thread."
              : locate.isError
                ? "This message was deleted, or you can't see it."
                : (messages.isPending && focus.thread && threadId) || locate.isFetching
                  ? "Loading the thread…"
                  : "Click a message to open its thread here."}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  const opts = {
    chat,
    replies: new Map(),
    runs,
    inFlight: [],
    seats,
    now: new Date(),
    suggestions,
  };
  const [root] = toChatMessages([rootRow], opts);
  if (!root) return null;
  const canWrite = !chat.readOnly && chat.meId !== null;
  // CHAT-THREAD-AGENTS: the agents answering in this thread say so here, at its bottom (the
  // channel's main line only shows "… is replying…" on the root).
  const conversation = [rootRow, ...replies];
  const live = runsInTopic(runs, "", new Set(conversation.map((m) => m.id)));
  const inThread = threadRuns(live, conversation, rootRow.id, dmLike);
  const statusLine = (
    <>
      {chat.agents ? <AgentStatusLine chat={chat} runs={inThread} typing={[]} /> : null}
      <AgentWorkingLine
        messages={threadAsks(conversation, rootRow.id, dmLike)}
        skip={chat.agents ? inThread.map((r) => r.membership_id) : []}
      />
    </>
  );
  const panel = (
    <FileDropZone
      onFiles={staged.add}
      disabled={!canWrite}
      label="Drop files to attach to your reply"
      // CHAT-PANEL: the panel's body gives a thread its whole height (a `flush` tab); only the
      // reply list scrolls, inside ThreadPanel.
      className="flex min-h-0 flex-1 flex-col"
    >
      <ThreadPanel
        data-panel="thread"
        className="min-h-0 flex-1"
        root={root}
        replies={toChatMessages(replies, opts)}
        highlightId={linked ?? undefined}
        context={`${channelLabel(channel.data, chat)}${topic && channel.data.kind !== "dm" ? ` › ${topic.name}` : ""}`}
        currentUserId={chat.meId ?? undefined}
        statusLine={statusLine}
        headerExtra={
          threadId ? <ThreadAgentChips chat={chat} threadId={threadId} root={rootRow.id} /> : null
        }
        following={following}
        {...(chat.meId
          ? {
              onFollowingChange: (on: boolean) => {
                setOverride({ root: rootRow.id, on });
                readActions.setFollowing(threadId, rootRow.id, on);
              },
            }
          : {})}
        {...actions.listProps}
        composer={
          canWrite ? (
            <div className="flex flex-col gap-2">
              {remind.dialog}
              {later.dialog}
              <AddresseeChip addressee={addressee} />
              <Composer
                key={replyDraft?.mount}
                {...(replyDraft ? { draft: replyDraft.draft } : {})}
                placeholder="Reply…"
                label="Reply"
                hint={false}
                mentionables={mentionables}
                commands={remind.command ? [remind.command] : []}
                attachments={staged.items.length ? <StagedStrip staged={staged} /> : undefined}
                onFiles={staged.add}
                hasFiles={staged.ready}
                uploading={staged.uploading}
                {...(later.onSchedule ? { onSchedule: later.onSchedule } : {})}
                onSubmit={async (value) => {
                  // A personal `/remind …` is never posted to the thread.
                  if (await remind.intercept(value)) return;
                  const files = staged.take();
                  try {
                    await send(threadId, {
                      value: withDraftKey(value, replyDraft?.key),
                      files,
                      parent: rootRow.id,
                      addressee: addressee.outgoing(),
                      alsoToChannel: alsoChannel,
                    });
                  } catch (e) {
                    staged.putBack(files);
                    throw e;
                  }
                  addressee.sent();
                  // CHAT-THREAD-AGENTS: the server posts the channel's copy with the reply (a note
                  // that wakes no agent, quoting the root): the main line refetches to show it.
                  if (alsoChannel)
                    void queryClient.invalidateQueries({
                      queryKey: chatKeys.messages(co, threadId),
                    });
                }}
              />
              {channel.data.kind !== "dm" && channel.data.kind !== "group_dm" ? (
                <Field orientation="horizontal" className="gap-2">
                  <Checkbox
                    id="thread-also-channel"
                    checked={alsoChannel}
                    onCheckedChange={(on) => setAlsoChannel(Boolean(on))}
                  />
                  <FieldLabel
                    htmlFor="thread-also-channel"
                    className="text-xs font-normal text-muted-foreground"
                  >
                    {/* CHAT-TOPICS-SEPARATE: the copy goes to this place's main line. */}
                    Also send to{" "}
                    {topic && !topic.is_main ? topic.name : channelLabel(channel.data, chat)}
                  </FieldLabel>
                </Field>
              ) : null}
            </div>
          ) : null
        }
      />
      {actions.dialog}
    </FileDropZone>
  );
  if (!mobile || typeof document === "undefined") return panel;
  // A phone: the thread takes the whole screen, with Back to the conversation.
  return createPortal(
    <div
      data-slot="thread-fullscreen"
      className="fixed inset-0 z-drawer flex flex-col bg-card text-card-foreground"
    >
      <div className="flex h-11 shrink-0 items-center border-b border-border px-1">
        <Button variant="ghost" size="sm" className="pointer-coarse:h-11" onClick={close}>
          <ArrowLeftIcon data-icon="inline-start" />
          Back
        </Button>
      </div>
      {panel}
    </div>,
    document.body,
  );
}

/** Open popups that take Escape for themselves. */
const POPUPS = [
  '[role="menu"]',
  '[role="listbox"]',
  '[role="dialog"][aria-modal="true"]',
  '[role="alertdialog"]',
  '[data-slot="popover-content"]',
].join(",");
